<!-- @format -->
<template>
    <div class="email-inbox-connect-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ steps[step].title }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="step === 'email'" class="choose-account-container">
                    <div class="widget-description">
                        <h4>Keep track of your email activity in GlassHive</h4>
                        <p>
                            Connect your mailbox to begin sending and receiving emails with contacts
                            in GlassHive. Only email conversations with existing contacts will be
                            captured.
                        </p>
                    </div>
                    <div class="account-cards">
                        <div
                            class="microsoft-account account-card"
                            @click="connectMicrosoftAccount"
                        >
                            <img
                                src="@/assets/images/EmailInbox/microsoft_logo.png"
                                alt="microsoft logo"
                            />
                            <p class="connect-microsoft">Connect Microsoft 365</p>
                        </div>
                        <div
                            class="google-account account-card"
                            @click="connectGoogleAccount"
                        >
                            <img
                                src="@/assets/images/EmailInbox/gmail_logo.png"
                                alt="google logo"
                            />
                            <p class="connect-gmail">Connect Gmail</p>
                        </div>
                    </div>
                </div>

                <div v-if="step === 'connect'">
                    <div class="connect-banner">
                        <img
                            v-if="account === 'microsoft'"
                            src="@/assets/images/EmailInbox/microsoft_integration_graphic.png"
                            alt="microsoft banner"
                        />
                        <img
                            v-if="account === 'google'"
                            src="@/assets/images/EmailInbox/google_integration_graphic.png"
                            alt="google banner"
                        />
                    </div>
                    <span class="email-provider-name">{{
                        accountInfo[account].emailProviderName
                    }}</span>
                    <div class="banefits-container bordered-group">
                        <label>Benefits</label>
                        <ul class="list-benefits">
                            <li
                                v-for="(benefit, index) in benefits"
                                :key="'_benefit_' + index"
                                class="list"
                            >
                                <span class="checkmark">✓</span>
                                {{ benefit.value }}
                            </li>
                        </ul>
                    </div>
                    <div class="details-container bordered-group">
                        <label>Details</label>
                        <div class="details" v-html="accountInfo[account].details"></div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn transparent-btn" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div v-if="step != 'email'" class="actions-container">
                    <app-button class="back-btn transparent-blue-btn" @click="goBack">
                        Back
                    </app-button>
                    <app-button
                        :class="['accept-btn', account === 'microsoft' ? 'microsoft' : 'google']"
                        size="constant-size"
                        @click="openProviderPopup"
                    >
                        <div class="logo-container">
                            <img
                                :src="
                                    require(`@/assets/images/EmailInbox/${accountInfo[account].logo}`)
                                "
                                alt="provider logo"
                            />
                        </div>
                        Sign in with {{ accountInfo[account].signInButtonName }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
import { EmailInboxService } from '@/scripts/services/email-inbox/email-inbox-service';
import EventBus from '@/scripts/event-bus.js';
import LoadingPopup from '@/components/popups/loading-popup';
import log from '@/scripts/logger';

export default {
    name: 'email-inbox-connect-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            account: null,
            loading: true,
            accountInfo: {
                google: {
                    title: 'Google Account',
                    emailProviderName: 'Google / Gmail',
                    name: 'Gmail',
                    logo: 'google_logo.png',
                    signInButtonName: 'Google',
                    details:
                        '<p>Integrating your Google account with GlassHive will connect your Gmail services to your GlassHive account. With this integration, GlassHive will have access to some information about your emails, such as the email address you’re sending from, the email address of the recipients, the email subject line, and the body of the email. In addition, you can send and receive Gmail messages, schedule email sends and modify email drafts in the GlassHive app. <br><br>If you wish to discontinue the integration, you can simply remove it by navigating to Your Profile > Integrations > Email Integrations and click ‘Disable’. You can also revoke GlassHive’s access from within your Google account by following the instructions listed here. If you need more information visit www.glasshive.com/security to learn about our security policies and read the FAQ page about the GlassHive Gmail integration.<br><br></p>',
                },

                microsoft: {
                    title: 'Microsoft 365 Inbox',
                    emailProviderName: 'Microsoft 365',
                    name: 'Microsoft 365',
                    logo: 'microsoft_logo.png',
                    signInButtonName: 'Microsoft',
                    details:
                        '<p>Authorizing GlassHive access to your inbox will allow us to locate relevant email conversations and log them in GlassHive, allowing your team to work on them together. Direct emails sent from GlassHive will appear in your Outlook sent folder. <br><br> With this integration, GlassHive will have access to some information about your emails, such as the email address you’re sending from, the email address of the recipients, the email subject line, and the body of the email. This access is only used to enhance your GlassHive experience and make your process simpler. The data belongs to you.<br><br> You’ll always have the ability to delete specific email records from GlassHive. You’ll have the ability to make changes on how your integration works - like removing the integration or adding another email account - by adjusting the settings. You have all the control.<br><br></p>',
                },
            },

            step: null,

            steps: {
                email: {
                    title: 'Email',
                    previous: 'email',
                },

                connect: {
                    title: null,
                    previous: 'email',
                },
            },

            benefits: [
                {
                    value: 'Send and schedule emails from GlassHive ',
                },
                {
                    value: 'Log email replies to GlassHive automatically',
                },
            ],
        };
    },

    watch: {
        loading() {
            this.$modal.hide('loading-popup');
            this.loading = true;
        },
    },

    created() {
        this.step = 'email';
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        goBack() {
            this.step = this.steps[this.step].previous;
        },

        connectGoogleAccount() {
                this.step = 'connect';
                this.account = 'google';
                this.steps[this.step].title = 'Connect your Google Account';
        },

        connectMicrosoftAccount() {
            this.step = 'connect';
            this.account = 'microsoft';
            this.steps[this.step].title = 'Connect your Microsoft 365 Inbox';
        },

        openProviderPopup() {
            this.createConnectPopUp();
            this.getAuth(this.account);
        },

        showError() {
            this.$helpers.showFailNotification(
                `An error occurred during the authorization process with ${
                    this.account.charAt(0).toUpperCase() + this.account.slice(1)
                }`
            );
            this.loading = false;
        },

        getAuth(provider) {
            EmailInboxService.getAuth(provider)
                .then((response) => {
                    if (response.ok) {
                        return response.json();
                    }

                    this.showError();
                })
                .then((data) => {
                    window.open(data.url, '_self');
                })
                .catch((error) => {
                    log.error('An error occurred while fetching the authorization URL', error);
                    this.showError();
                });
        },

        createConnectPopUp() {
            this.$modal.show(
                LoadingPopup,
                {
                    title: 'Connecting...',
                    loading: this.loading,
                    props: ['name'],
                },
                {
                    name: 'loading-popup',
                },
                {
                    classes: 'lightbox-popup',
                    height: '100%',
                    width: '803px',
                }
            );
        },
    },
};
</script>

<style>
.email-inbox-connect-widget.widget.purple-gradient-header .widget-header {
    background: linear-gradient(264deg, #e38dee, #7a5bff);
}

.email-inbox-connect-widget .widget-contents-container .content-wrapper {
    padding-top: 26px;
}

.email-inbox-connect-widget .widget-contents-container .email-provider-name {
    display: flex;
    justify-content: center;
    margin-top: 16px;
    margin-bottom: 40px;
    text-align: center;
    font: normal normal bold 16px/25px Roboto;
    letter-spacing: 0px;
    color: #382153;
}

.email-inbox-connect-widget .widget-contents-container .content-wrapper .blue {
    color: #18aeff;
}

.email-inbox-connect-widget .widget-description h4 {
    text-align: left;
    font: normal normal bold 16px/15px Nunito;
    letter-spacing: 0px;
    color: #382153;
}

.email-inbox-connect-widget .widget-description p {
    text-align: left;
    font: normal normal normal 14px/22px Roboto;
    letter-spacing: 0px;
    color: #382153;
}

.email-inbox-connect-widget .widget-contents-container .transparent-btn {
    background: transparent;
    color: #382153;
    border: 1px solid #cac6cb;
    border-radius: 20px;
    width: 120px;
}

.email-inbox-connect-widget .widget-contents-container .transparent-blue-btn {
    background: transparent;
    color: #18aeff;
    border: 1px solid #18aeff;
    border-radius: 20px;
    width: 100px;
}

.email-inbox-connect-widget
    .widget-contents-container
    .content-wrapper
    .account-cards
    .account-card {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    background: #ffffff 0% 0% no-repeat padding-box;
    border: 2px solid #e9e9ef;
    border-radius: 15px;
    height: 165px;
    margin-top: 22px;
}

.email-inbox-connect-widget
    .widget-contents-container
    .content-wrapper
    .account-cards
    .account-card
    p {
    text-align: center;
    font: normal normal bold 20px/15px Nunito;
    letter-spacing: 0px;
    color: #382153;
    margin-top: 12px;
}

.email-inbox-connect-widget
    .widget-contents-container
    .content-wrapper
    .account-cards
    .account-card:hover {
    background: #ffffff 0% 0% no-repeat padding-box;
    border: 2px solid #18aeff;
    border-radius: 15px;
    cursor: pointer;
}

.email-inbox-connect-widget
    .widget-contents-container
    .content-wrapper
    .account-cards
    .gray-out.google-account {
    pointer-events: none;
    opacity: 0.5;
}

.email-inbox-connect-widget
    .widget-contents-container
    .content-wrapper
    .account-cards
    .google-account
    img {
    width: 88px;
    margin-bottom: 12.5px;
}

.email-inbox-connect-widget
    .widget-contents-container
    .content-wrapper
    .account-cards
    .microsoft-account
    img {
    width: 65px;
    margin-bottom: 17px;
}

.email-inbox-connect-widget .widget-contents-container .content-wrapper .connect-banner {
    display: flex;
    align-content: center;
    justify-content: center;
}

.email-inbox-connect-widget .widget-contents-container .content-wrapper .connect-banner img {
    object-fit: cover;
    height: 105px;
    width: 310px;
}

.email-inbox-connect-widget .bordered-group {
    border-radius: 20px;
    padding: 21px 20px 17px 30px;
    border: 2px solid #e9e9ef;
    position: relative;
    margin-top: 28px;
    text-align: left;
    font: normal normal normal 14px/25px Roboto;
    letter-spacing: 0px;
    color: #382153;
}

.email-inbox-connect-widget .bordered-group ul {
    margin-bottom: 0px;
}

.email-inbox-connect-widget .bordered-group ul li .checkmark {
    color: #7a5bff;
    margin-right: 0.5em;
}

.email-inbox-connect-widget .bordered-group label {
    position: absolute;
    top: -18px;
    left: calc(50% - 65px);
    background: #ffffff;
    display: block;
    padding: 0 30px;
    margin: 0;
    text-align: center;
    font: normal normal bold 16px/35px Roboto;
    letter-spacing: 0px;
    color: #382153;
    width: 131px;
}

.email-inbox-connect-widget .details-container .details p {
    margin-bottom: 0px;
}

.email-inbox-connect-widget .details-container p {
    margin-bottom: 20px;
}

.email-inbox-connect-widget .actions-container {
    display: flex;
}

.email-inbox-connect-widget .actions-container .accept-btn {
    height: 40px;
    margin-left: 12px;
    font-size: 14px;
    font-weight: 500;
    padding: 1px 8px 1px 1px;
    border: 1px solid transparent;
}

.email-inbox-connect-widget .actions-container .accept-btn.google {
    background: #4285f4;
}

.email-inbox-connect-widget .actions-container .accept-btn.microsoft {
    padding-right: 12px;
}

.email-inbox-connect-widget .actions-container .accept-btn .logo-container {
    height: 100%;
    display: flex;
    align-items: center;
    background: white;
    border-top-left-radius: 20px;
    border-bottom-left-radius: 20px;
}

.email-inbox-connect-widget .actions-container .accept-btn.google .logo-container {
    padding-left: 8px;
    margin-right: 12px;
}

.email-inbox-connect-widget .actions-container .accept-btn.microsoft .logo-container {
    padding-left: 12px;
    margin-right: 6px;
}

.email-inbox-connect-widget .actions-container .accept-btn img {
    width: 18px;
    height: 18px;
}

.email-inbox-connect-widget .actions-container .accept-btn.google img {
    margin-right: 12px;
}

.email-inbox-connect-widget .actions-container .accept-btn.microsoft img {
    margin-right: 6px;
}
</style>
